/**
 * BundleSnapshotsList (Iter 36.j)
 *
 * Renders below the bundle action buttons. Lists active + expired/revoked
 * snapshot tokens for the workspace, with view counts + revoke buttons.
 * Compact card surface — when there are zero snapshots we collapse to a
 * single empty-state line so the dashboard doesn't feel cluttered for
 * first-time users.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShareNetworkIcon as ShareNetwork,
  TrashIcon as Trash,
  EyeIcon as Eye,
  ClockIcon as Clock,
  CopyIcon as Copy,
  CheckCircleIcon as CheckCircle,
  ArrowSquareOutIcon as ArrowSquareOut,
} from "@phosphor-icons/react";
import { listBundleSnapshots, revokeBundleSnapshot } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

function snapshotUrl(token) {
  return `${window.location.origin}/share/bundle/${token}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ row }) {
  if (row.revoked) {
    return (
      <span
        className="ml-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
        style={{ background: "#f4f4f4", color: "#525252", borderColor: "#c6c6c6" }}
        data-testid={`bundle-snapshot-status-revoked-${row.token_suffix}`}
      >
        Revoked
      </span>
    );
  }
  if (row.is_expired) {
    return (
      <span
        className="ml-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
        style={{ background: "#fff1f1", color: "#a2191f", borderColor: "#ffd7d9" }}
        data-testid={`bundle-snapshot-status-expired-${row.token_suffix}`}
      >
        Expired
      </span>
    );
  }
  return (
    <span
      className="ml-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1"
      style={{ background: "#defbe6", color: "#0e6027", borderColor: "#a7f0ba" }}
      data-testid={`bundle-snapshot-status-active-${row.token_suffix}`}
    >
      <CheckCircle size={9} weight="bold" /> Active
    </span>
  );
}

function SnapshotRow({ row, onRevoke, canWrite }) {
  const recipient = row.recipient_email || row.recipient_label || (
    <span className="text-[var(--ml-text-secondary)] italic">(no recipient)</span>
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snapshotUrl(row.token));
      toast.success("Snapshot URL copied.");
    } catch {
      toast.error("Couldn't access clipboard — copy the URL manually.");
    }
  };

  const handleRevoke = () => {
    if (!window.confirm(
      "Revoke this snapshot? The link will stop working immediately for any recipient who has it.",
    )) return;
    onRevoke(row.token);
  };

  return (
    <div
      className="grid grid-cols-12 gap-3 items-center py-2 border-b border-[var(--ml-border)] text-sm"
      data-testid={`bundle-snapshot-row-${row.token_suffix}`}
    >
      <div className="col-span-2 flex items-center gap-2">
        <StatusPill row={row} />
        <code className="ml-mono text-[10px] text-[var(--ml-text-secondary)]">…{row.token_suffix}</code>
      </div>
      <div className="col-span-4 truncate">
        <div className="truncate">{recipient}</div>
        {row.label && (
          <div className="ml-mono text-[10px] text-[var(--ml-text-secondary)] truncate">
            {row.label}
          </div>
        )}
      </div>
      <div className="col-span-2 ml-mono text-xs text-[var(--ml-text-secondary)] flex items-center gap-1">
        <Eye size={11} weight="bold" />
        <span data-testid={`bundle-snapshot-views-${row.token_suffix}`}>{row.view_count}</span>
        {row.view_count > 0 && (
          <span className="text-[10px] opacity-70">
            ({row.views_via_browser}b·{row.views_via_pdf}p)
          </span>
        )}
      </div>
      <div className="col-span-2 ml-mono text-[11px] text-[var(--ml-text-secondary)] flex items-center gap-1">
        <Clock size={10} weight="bold" />
        {fmtDate(row.expires_at)}
      </div>
      <div className="col-span-2 flex items-center justify-end gap-1">
        {!row.revoked && !row.is_expired && (
          <>
            <button
              type="button"
              onClick={handleCopy}
              className="ml-btn-icon"
              title="Copy URL"
              data-testid={`bundle-snapshot-copy-${row.token_suffix}`}
            >
              <Copy size={13} weight="bold" />
            </button>
            <a
              href={snapshotUrl(row.token)}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-btn-icon"
              title="Open preview"
              data-testid={`bundle-snapshot-open-${row.token_suffix}`}
            >
              <ArrowSquareOut size={13} weight="bold" />
            </a>
            {canWrite && (
              <button
                type="button"
                onClick={handleRevoke}
                className="ml-btn-icon text-[#a2191f]"
                title="Revoke snapshot"
                data-testid={`bundle-snapshot-revoke-${row.token_suffix}`}
              >
                <Trash size={13} weight="bold" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function BundleSnapshotsList({ workspaceId, canWrite, refreshKey }) {
  const [rows, setRows] = useState(null);

  const reload = useCallback(() => {
    listBundleSnapshots(workspaceId)
      .then(setRows)
      .catch(() => setRows([]));
  }, [workspaceId]);

  useEffect(() => {
    reload();
  }, [reload, refreshKey]);

  const handleRevoke = async (token) => {
    try {
      await revokeBundleSnapshot(workspaceId, token);
      toast.success("Snapshot revoked.");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not revoke snapshot.");
    }
  };

  if (rows === null) return null;

  if (rows.length === 0) {
    return (
      <div
        className="ml-card p-4 text-sm text-[var(--ml-text-secondary)] flex items-center gap-2"
        data-testid="bundle-snapshots-empty"
      >
        <ShareNetwork size={14} weight="bold" className="text-[var(--ml-text-secondary)]" />
        No public snapshot links yet. Click <strong>Share snapshot</strong> above to mint one.
      </div>
    );
  }

  return (
    <div className="ml-card p-4 space-y-2" data-testid="bundle-snapshots-list">
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
        <ShareNetwork size={11} weight="bold" /> Public snapshots ({rows.length})
      </div>
      <div className="grid grid-cols-12 gap-3 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] pb-1 border-b border-[var(--ml-border)]">
        <div className="col-span-2">Status</div>
        <div className="col-span-4">Recipient · Label</div>
        <div className="col-span-2">Views</div>
        <div className="col-span-2">Expires</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      {rows.map((row) => (
        <SnapshotRow
          key={row.token}
          row={row}
          onRevoke={handleRevoke}
          canWrite={canWrite}
        />
      ))}
    </div>
  );
}
